import * as React from "react"
import { useCallback, useEffect, useMemo, useReducer, useState } from "react"
import { observer } from "mobx-react-lite"
import RNBluetoothClassic, { BluetoothDevice } from "react-native-bluetooth-classic"
import { Picker, PickerProps } from "../picker/picker"
import _ from "lodash"
import { Icon, Spinner, Toast, useDisclose, View } from "native-base"
import {
  requestAccessFineLocationPermission,
  requestBluetoothConnectPermission,
  requestBluetoothScanPermission
} from "../../utils/permissions";
import { Appearance, Dimensions, Platform, ViewStyle, TouchableOpacity } from "react-native"
import { Text } from "../text/text"
import MaterialIcons from "react-native-vector-icons/MaterialIcons"
import MaterialCommunityIcons from "react-native-vector-icons/MaterialCommunityIcons"
import { Actionsheet } from "../actionsheet/actionsheet"
import { ListRenderItem } from "../flat-list/flat-list"
import { QRCodeScanner } from "../qr-code-scanner/qr-code-scanner"

const ACTION_SHEET_ITEM: ViewStyle = {
  height: 52,
  alignItems: "stretch",
  justifyContent: "center",
  paddingHorizontal: 16,
}

const QR_INFO: ViewStyle = {
  flex: 0,
  height: 0
}

const QR_CONTAINER: ViewStyle = {
  alignItems: "center"
}

export interface BluetoothPickerProps extends Omit<PickerProps, "data"> {
  data?: any[],
  onPressBluetooth?: () => void,
  onPressQRScan?: () => void
}

const modelReducers = (state: Array<BluetoothDevice>, action: { type: "set" | "merge" | "clear", payload?: Array<BluetoothDevice>|BluetoothDevice }) => {
  const payload = (action.payload instanceof Array && action.payload) || (action.payload instanceof BluetoothDevice && [action.payload])
  switch (action.type) {
    case 'set':
      return _.orderBy(_.uniqBy(payload, (d) => d.id), (d) => `${d.bonded ? 0 : 1}${d.name || ''}${d.id}`)
    case 'merge':
      return _.orderBy(_.uniqBy([...payload, ...state], (d) => d.id), (d) => `${d.bonded ? 0 : 1}${d.name || ''}${d.id}`)
    case 'clear':
      return []
    default:
      return state
  }
}

/**
 * Describe your component here
 */
export const BluetoothPicker = observer(function BluetoothPicker(props: BluetoothPickerProps) {
  const {
    value, dataKey = "id", dataLabel = "name",
    colorScheme = Appearance.getColorScheme(),
    actionSheetColorScheme = colorScheme,
    pickerActionsheetProps, onValueChange, isDisabled, onPressBluetooth,
    onPressQRScan, ...rest
  } = props

  const [ models, dispatchModels ] = useReducer(modelReducers, [])
  const [ search, setSearch ] = useState<string>("")
  const [ bluetoothEnabled, setBluetoothEnabled ] = useState<boolean>(false)
  const [ bluetoothGranted, setBluetoothGranted ] = useState<boolean>(false)
  const [ discovering, setDiscovering ] = useState<boolean>(false)

  const { isOpen, onOpen, onClose } = useDisclose(false)
  const { isOpen: isOpenQr, onClose: onCloseQr } = useDisclose(false)

  const filteredModels = useMemo<Array<any>>(() => (
    models.filter((m: BluetoothDevice) => (m.id.includes(search) || (m.name || '').toLowerCase().includes(search.toLowerCase())))
  ), [search, models])

  const getDevices = useCallback(async () => {
    let granted = await requestBluetoothConnectPermission()
      .then(() => requestBluetoothScanPermission())
      .catch((e) => {
        Toast.show({ title: e.message });
        return false;
      });
    if (!granted) return
    const devices = await RNBluetoothClassic.getBondedDevices().catch((e) => {
      Toast.show({ title: e.message });
      return [];
    });
    dispatchModels({ type: "merge", payload: devices })
    granted = await requestAccessFineLocationPermission().catch((e) => {
      Toast.show({ title: e.message });
      return false;
    });
    if (!granted) return
    setBluetoothGranted(true);
    if (Platform.OS === 'android') {
      setDiscovering(true)
      const unpairedDevices = await RNBluetoothClassic.startDiscovery().catch((e) => {
        Toast.show({ title: e.message });
        return [];
      });
      setDiscovering(false)
      dispatchModels({ type: "merge", payload: unpairedDevices })
    }
  }, [])

  useEffect(() => {
    if (Platform.OS === 'web') return undefined;
    const bluetoothEnabled = RNBluetoothClassic.onBluetoothEnabled((event) => setBluetoothEnabled(event.enabled))
    const bluetoothDisabled = RNBluetoothClassic.onBluetoothDisabled((event) => setBluetoothEnabled(event.enabled))
    RNBluetoothClassic.isBluetoothEnabled().catch(() => false).then((enabled) => {
      getDevices().catch()
      setBluetoothEnabled(enabled)
    })
    return () => {
      bluetoothEnabled.remove()
      bluetoothDisabled.remove()
    }
  }, [getDevices])

  useEffect(() => {
    if (bluetoothEnabled && bluetoothGranted) {
      if (!isOpen) RNBluetoothClassic.cancelDiscovery().catch()
    }
  }, [bluetoothEnabled, bluetoothGranted, getDevices, isOpen])

  const initializeSelectedDatum = useCallback((callback) => {
    if (value) callback(models.find((m) => m.id === value))
  }, [value, models])

  const renderSelectedItem = useCallback((item) => {
    if (!item) return null;
    return (
      <View style={ACTION_SHEET_ITEM}>
        <Text text={item.name} preset={item.id === value ? "title4" : "body2"} colorScheme={actionSheetColorScheme} isTruncated />
        {item.name !== item.id && <Text text={item.id} preset="body4" colorScheme={actionSheetColorScheme} isTruncated />}
      </View>
    )
  }, [actionSheetColorScheme, value])

  const renderItem: ListRenderItem<any> = useCallback(({ item }) => {
    return renderSelectedItem(item)
  }, [renderSelectedItem])

  const ListFooterComponent = useCallback(() => discovering && (
    <Spinner height="52px"/>
  ), [discovering])

  const onReadQr = useCallback(async ({ data }) => {
    const connected = await RNBluetoothClassic.isDeviceConnected(data).catch(() => false)
    const device = connected ?
      await RNBluetoothClassic.getConnectedDevice(data).catch(() => null) :
      await RNBluetoothClassic.connectToDevice(data).catch(() => null)
    if (device) onValueChange(device.id, 0, device)
    await onCloseQr()
  }, [onCloseQr, onValueChange])

  return (
    <>
      <Picker
        value={value}
        data={filteredModels}
        dataKey={dataKey}
        dataLabel={dataLabel}
        renderSelectedItem={renderSelectedItem}
        pickerActionsheetProps={{
          inputProps: { onChangeText: setSearch },
          renderItem,
          flatListProps: {
            ListFooterComponent,
            estimatedItemSize: 52,
            getItemLayout: (data, index) => ({ length: 52, offset: 52 * index, index }),
          },
          ...pickerActionsheetProps
        }}
        initializeSelectedDatum={initializeSelectedDatum}
        onOpen={onOpen}
        onClose={onClose}
        actionSheetColorScheme={actionSheetColorScheme}
        colorScheme={colorScheme}
        InputRightElement={(
          <View flexDirection="row" alignItems="center">
            <Icon as={<MaterialIcons name="bluetooth-audio" />} testID={`${(rest.testID || "")}-qr-input`}
                  size={"24px"} mr={"9px"} textAlign="center" backgroundColor={rest.backgroundColor} color="#666666"
                  onPress={onPressBluetooth}
            />
            <TouchableOpacity style={QR_CONTAINER}>
              <Icon as={<MaterialCommunityIcons name="line-scan" />} testID={`${(rest.testID || "")}-qr-input`}
                    size={"24px"} mr={"9px"} textAlign="center" backgroundColor={rest.backgroundColor} color="#666666"
                    disabled={isDisabled}
                    onPress={onPressQRScan}
              />
            </TouchableOpacity>
          </View>
        )}
        onValueChange={onValueChange}
        isDisabled={isDisabled}
        {...rest}
      ></Picker>
      <Actionsheet
        colorScheme={colorScheme}
        isOpen={isOpenQr}
        onClose={onCloseQr}
        titleTx="bluetoothPicker.scanQr"
        actionsheetStyle={{ minHeight: Dimensions.get("window").width + 54 }}
      >
        <QRCodeScanner
          onRead={onReadQr}
          showMarker
          cameraProps={{ ratio: '1:1', autoFocus: "on" }}
          topViewStyle={QR_INFO}
          bottomViewStyle={QR_INFO}
          fadeIn={false}
        />
      </Actionsheet>
    </>
  )
})
